import { parseLocal, shiftLocal } from "./time";
import { type GameLabel, parseCanonicalNote, type ScheduledGame } from "./types";

/**
 * How long a game takes, and how long the pitch is busy.
 *
 * THIS DID NOT EXIST, and its absence was visible from the outside. A
 * `ScheduledGame` has a start and no duration, so everything downstream was
 * guessing: `checkSchedule` inferred a slot length from the median gap between
 * consecutive games on a pitch, and the schedule assistant — which has no
 * median to look at while it is deciding where to put something — simply made a
 * number up, differently each time. Asked to move a game to 17:00 it reported
 * the 16:50 game as running "16:50–17:10", then "17:50–18:40" a moment later.
 * Neither figure came from anywhere.
 *
 * A tournament already knows these numbers; nothing was asking for them. So
 * they are settings now (`SlotSettings` in `shared/settings.ts`), and this is
 * the one place that turns them into an answer for a particular game.
 *
 * The distinction that matters and is easy to lose:
 *
 *   PLAYING TIME is how long the teams are on the pitch, halftime included.
 *   TURNAROUND is the pitch being free afterwards — one side finishing, the
 *     next two warming up, spirit circles.
 *   REST is what a TEAM gets between its own two games, which is a different
 *     measurement in a different direction and is why it is separate.
 *
 * Pure, and tested. Games end on a score rather than a clock, so every number
 * here is a PLAN, not a fact — which is exactly what a schedule is.
 */

/**
 * One division's own playing lengths, each one optional.
 *
 * An absent field INHERITS the tournament-wide number rather than defaulting
 * to anything of its own: an event that runs Open at 80 and Women's at 60 says
 * two numbers, not ten, and a division nobody has touched keeps following the
 * top of the page when that changes.
 *
 * Only the three PLAYING lengths. `turnaround` is a property of the PITCH —
 * the same pitch takes the same time to clear whoever just walked off it — and
 * `rest` is a property of a TEAM, measured between that team's own two games.
 * Neither belongs to a division, so neither is overridable here.
 */
export interface DivisionSlots {
  pool?: number;
  bracket?: number;
  final?: number;
}

export interface SlotSettings {
  /** Playing minutes for a pool game, halftime included. */
  pool: number;
  /** ...for a bracket game. */
  bracket: number;
  /** ...for a final. Finals are longer at most events, and scheduling one at
   *  pool length is how a day overruns at the end, in front of everybody. */
  final: number;
  /** The pitch standing empty between two games on it. */
  turnaround: number;
  /** The least a TEAM should get between its own games before the checker
   *  says something. Not a pitch measurement: see the module note. */
  rest: number;
  /**
   * Per-division overrides of the three above, keyed by division id.
   *
   * Optional, and absent on every row written before divisions could differ.
   * A key for a division that no longer exists is inert rather than wrong: it
   * matches no game, so it changes no answer.
   */
  byDivision?: Record<string, DivisionSlots>;
}

/** What this app's events actually run. Sixty minutes with halftime inside it,
 *  ninety for a final, ten to clear the pitch, twenty before a team is asked to
 *  play again. */
export const DEFAULT_SLOTS: SlotSettings = {
  pool: 60,
  bracket: 60,
  final: 90,
  turnaround: 10,
  rest: 20,
};

/** Is this the game the tournament ends on? A grand final counts; a 3rd-place
 *  game does not — it is an ordinary bracket game played at the same time. */
function isFinal(game: Pick<ScheduledGame, "label" | "note">): boolean {
  const label: GameLabel | undefined = game.label ?? parseCanonicalNote(game.note);
  return label?.kind === "final" || label?.kind === "grandFinal";
}

/** Everything a duration can be asked about. `divisionId` is part of it
 *  because two divisions at one event legitimately play different lengths;
 *  `override` because one game can be its own length (a make-up game cut to
 *  sixty minutes) and every reader here must see the same number. */
export type Timed = Pick<ScheduledGame, "label" | "note" | "stage" | "divisionId" | "override">;

/**
 * The three playing lengths that apply to one game's division, with the
 * tournament-wide numbers standing in for anything it did not override.
 */
function lengthsFor(divisionId: string | undefined, slots: SlotSettings): Required<DivisionSlots> {
  const over = divisionId === undefined ? undefined : slots.byDivision?.[divisionId];
  return {
    pool: over?.pool ?? slots.pool,
    bracket: over?.bracket ?? slots.bracket,
    final: over?.final ?? slots.final,
  };
}

/** Playing minutes for one game: its own override first, then its
 *  division's terms. */
export function playMinutes(game: Timed, slots: SlotSettings): number {
  if (game.override !== undefined) return game.override.minutes;
  const len = lengthsFor(game.divisionId, slots);
  if (isFinal(game)) return len.final;
  return (game.stage ?? "pool") === "bracket" ? len.bracket : len.pool;
}

/** How long the PITCH is unavailable: the game plus clearing it afterwards.
 *  This is the number that decides whether two games on one pitch collide. */
export function occupiesMinutes(game: Timed, slots: SlotSettings): number {
  return playMinutes(game, slots) + slots.turnaround;
}

/** When the game is expected to end, as a stored local time. `null` when its
 *  start is not a time — this never invents one. */
export function endsAt(game: Timed & Pick<ScheduledGame, "startAt">, slots: SlotSettings): string | null {
  return shiftLocal(game.startAt, playMinutes(game, slots));
}

/**
 * Do these two games want the same pitch at the same time?
 *
 * Only answerable since durations became settings — before, nothing knew how
 * long a game occupied a pitch, so nothing could tell. It is what stops a
 * generator laying a second division on top of the first: the assistant did
 * exactly that, put both divisions on both pitches from 09:00, and produced
 * twenty-six warnings in one call.
 *
 * The turnaround counts: a pitch is not free the instant the last point lands.
 */
export function clashesOnPitch(
  a: Timed & Pick<ScheduledGame, "startAt" | "fieldId">,
  b: Timed & Pick<ScheduledGame, "startAt" | "fieldId">,
  slots: SlotSettings,
): boolean {
  if (a.fieldId !== b.fieldId) return false;
  const startA = parseLocal(a.startAt)?.getTime();
  const startB = parseLocal(b.startAt)?.getTime();
  if (startA === undefined || startB === undefined) return false;
  const endA = startA + occupiesMinutes(a, slots) * 60_000;
  const endB = startB + occupiesMinutes(b, slots) * 60_000;
  return startA < endB && startB < endA;
}

/** The earliest the NEXT game on the same pitch may start. */
export function freeAt(game: Timed & Pick<ScheduledGame, "startAt">, slots: SlotSettings): string | null {
  return shiftLocal(game.startAt, occupiesMinutes(game, slots));
}
